import type { SystemStyleObject } from "@chakra-ui/react";

import type { Theme, WidgetStyleCategory } from "@/types";
import {
  appearanceAlertLinkSx,
  APPEARANCE_ALERT_LINK_SELECTOR,
  appearanceBreadcrumbSx,
  appearanceLinkSx,
  appearancePageSx,
  getThemeAppearanceDefaults,
  resolveAppearanceColors,
  type AppearanceColors
} from "@/helpers/appearance-colors";
import {
  readSiteThemeColors,
  resolveAccentColor,
  resolveBrandColor,
  type SiteThemeColors
} from "@/helpers/theme-api-colors";

export type ProjectPageThemeId = WidgetStyleCategory;

export interface WidgetStyles {
  category: WidgetStyleCategory;
  listContainerSx: SystemStyleObject;
  containerSx: SystemStyleObject;
  accordionButtonSx: SystemStyleObject;
  titleSx: SystemStyleObject;
  panelSx: SystemStyleObject;
  listItemSx: SystemStyleObject;
  contentCardSx: SystemStyleObject;
  lifeCycleCardSx: SystemStyleObject;
  subHeadingSx: SystemStyleObject;
}

export interface ProjectPageTheme {
  id: ProjectPageThemeId;
  appearance: AppearanceColors;
  siteColors: SiteThemeColors;
  pageSx: SystemStyleObject;
  titleSx: SystemStyleObject;
  toolBannerTitleSx: SystemStyleObject;
  bodySx: SystemStyleObject;
  descriptionCardSx: SystemStyleObject;
  breadcrumbSx: SystemStyleObject;
  toolsSx: SystemStyleObject;
  tabListSx: SystemStyleObject;
  tabSx: SystemStyleObject;
  tabPanelSx: SystemStyleObject;
  tabPanelContentSx: SystemStyleObject;
  widgetsColumnSx: SystemStyleObject;
  widgets: WidgetStyles;
  buttonSx: SystemStyleObject;
}

const CARD_SHADOW =
  "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03)";
const CARD_RADIUS = "sm";

const toolBannerTitleSx = (
  color: string,
  classic = false
): SystemStyleObject => ({
  color,
  fontSize: "2xl",
  fontWeight: classic ? "medium" : "bold",
  ...(classic ? { lineHeight: 1.4 } : {})
});

const classicPrimaryButtonSx = (accent: string): SystemStyleObject => ({
  bg: accent,
  color: "#fff",
  borderColor: accent,
  _hover: { bg: accent, color: "#fff" },
  _active: { bg: accent }
});

const classicInteractiveSx = (accent?: string): SystemStyleObject =>
  accent
    ? {
        "& a:not(.chakra-button):not(.chakra-breadcrumb__link)":
          appearanceLinkSx(accent),
        '& .chakra-button[data-variant="primary"]':
          classicPrimaryButtonSx(accent),
        '& .chakra-button[data-variant="link"]': appearanceLinkSx(accent),
        [APPEARANCE_ALERT_LINK_SELECTOR]: appearanceAlertLinkSx()
      }
    : {};

const resolveBrand = (
  brandColor?: string | null,
  siteTheme?: Partial<Theme> | SiteThemeColors | null
) =>
  resolveBrandColor({
    ...(siteTheme || {}),
    ...(typeof brandColor === "string" && brandColor ? { brandColor } : {})
  }) || "brand.500";

const widgetCardBg = (appearance: AppearanceColors, fallback: string): string =>
  appearance.widgetCardBackground || fallback;

const widgetBodyColor = (
  appearance: AppearanceColors,
  fallback = "#434343"
): string => appearance.widgetBodyTextColor || fallback;

const optionalColor = (color?: string): SystemStyleObject =>
  color ? { color } : {};

const optionalBg = (color?: string): SystemStyleObject =>
  color ? { bg: color } : {};

const toolBodyColor = (
  appearance: AppearanceColors,
  fallback = "#434343"
): string => appearance.toolBodyTextColor || fallback;

const toolCardBg = (appearance: AppearanceColors, fallback: string): string =>
  appearance.toolCardBackground || fallback;

const tabCardBg = (appearance: AppearanceColors, fallback: string): string =>
  appearance.tabBackgroundColor || fallback;

const activeTabText = (
  appearance: AppearanceColors,
  fallback = "#fff"
): string => appearance.toolTabActiveTextColor || fallback;

const toolMetaSx = (appearance: AppearanceColors): SystemStyleObject =>
  appearance.toolMetaTextColor
    ? {
        "& .meta, & .timestamp, & .project__publish-date": {
          color: appearance.toolMetaTextColor
        }
      }
    : {};

const classicWidgets = (
  accent?: string,
  bodySx: SystemStyleObject = {},
  appearance: AppearanceColors = {
    breadcrumbLinkColor: "",
    linkColor: ""
  }
): WidgetStyles => ({
  category: "classic",
  listContainerSx: {
    ...bodySx,
    ...classicInteractiveSx(accent),
    ...optionalBg(appearance.widgetBackgroundColor),
    ...optionalColor(appearance.widgetBodyTextColor)
  },
  containerSx: {
    border: "1px solid #ececec",
    bg: widgetCardBg(appearance, "white"),
    borderRadius: "md",
    overflow: "hidden"
  },
  accordionButtonSx: {
    px: 4,
    py: 3,
    bg: widgetCardBg(appearance, "#fff"),
    _hover: { bg: widgetCardBg(appearance, "#fff") },
    ...(accent ? { color: accent } : {})
  },
  titleSx: {
    fontWeight: "bold",
    fontSize: "md",
    ...(accent ? { color: accent } : {})
  },
  panelSx: {
    px: 4,
    py: 3,
    ...bodySx,
    ...optionalColor(appearance.widgetBodyTextColor)
  },
  listItemSx: {},
  contentCardSx: {
    border: "1px solid #ececec",
    bg: widgetCardBg(appearance, "white"),
    borderRadius: "md",
    overflow: "hidden",
    px: 4,
    py: 3,
    ...bodySx,
    ...classicInteractiveSx(accent),
    ...optionalColor(appearance.widgetBodyTextColor)
  },
  lifeCycleCardSx: {},
  subHeadingSx: {
    px: 2.5,
    py: 2.5,
    mb: 2,
    ...optionalColor(appearance.widgetBodyTextColor),
    "&:last-of-type:not(:only-of-type)": {
      mt: 3
    }
  }
});

const freshWidgets = (appearance: AppearanceColors): WidgetStyles => ({
  category: "fresh",
  listContainerSx: {
    px: 2.5,
    ...optionalBg(appearance.widgetBackgroundColor),
    ...optionalColor(appearance.widgetBodyTextColor)
  },
  containerSx: {
    border: "none",
    bg: "transparent",
    borderRadius: 0,
    overflow: "visible",
    boxShadow: "none"
  },
  accordionButtonSx: {
    px: 1,
    py: 3,
    bg: "transparent",
    _hover: { bg: "transparent" },
    _open: { bg: "transparent" }
  },
  titleSx: {
    fontWeight: "bold",
    fontSize: "lg",
    color: appearance.widgetTitleTextColor || "#434343"
  },
  panelSx: {
    px: 0,
    py: 3,
    bg: "transparent",
    color: widgetBodyColor(appearance)
  },
  listItemSx: {
    p: 4,
    mb: 3,
    border: "none",
    color: widgetBodyColor(appearance),
    bg: widgetCardBg(appearance, "#fff"),
    borderRadius: CARD_RADIUS,
    boxShadow: CARD_SHADOW,
    "&:last-child": {
      mb: 0
    }
  },
  contentCardSx: {
    bg: widgetCardBg(appearance, "white"),
    borderRadius: "9px",
    boxShadow: CARD_SHADOW,
    p: 5,
    border: "none",
    color: widgetBodyColor(appearance)
  },
  lifeCycleCardSx: {
    bg: widgetCardBg(appearance, "#fff"),
    borderRadius: CARD_RADIUS,
    boxShadow: CARD_SHADOW,
    p: 5,
    border: "none",
    color: widgetBodyColor(appearance)
  },
  subHeadingSx: {
    pl: 2.5,
    mb: 2,
    ...optionalColor(appearance.widgetBodyTextColor),
    "&:last-of-type:not(:only-of-type)": {
      mt: 3
    }
  }
});

const boldWidgets = (appearance: AppearanceColors): WidgetStyles => ({
  category: "bold",
  listContainerSx: {
    bg: appearance.widgetBackgroundColor || "#f3f3f3",
    borderRadius: CARD_RADIUS,
    p: 2.5,
    ...optionalColor(appearance.widgetBodyTextColor)
  },
  containerSx: {
    border: "none",
    bg: "transparent",
    borderRadius: 0,
    overflow: "visible",
    p: 0
  },
  accordionButtonSx: {
    px: 0,
    py: 3,
    bg: "transparent",
    _hover: { bg: "transparent" },
    _open: { bg: "transparent" }
  },
  titleSx: {
    fontWeight: "bold",
    fontSize: "lg",
    color: appearance.widgetTitleTextColor || "#434343"
  },
  panelSx: {
    px: 0,
    py: 3,
    bg: "transparent",
    color: widgetBodyColor(appearance)
  },
  listItemSx: {
    color: widgetBodyColor(appearance),
    bg: widgetCardBg(appearance, "white"),
    borderRadius: CARD_RADIUS,
    boxShadow: CARD_SHADOW,
    p: 4,
    mb: 5,
    border: "none",
    "&:last-child": {
      mb: 0
    }
  },
  contentCardSx: {
    bg: widgetCardBg(appearance, "white"),
    borderRadius: CARD_RADIUS,
    boxShadow: CARD_SHADOW,
    p: 4,
    border: "none",
    color: widgetBodyColor(appearance)
  },
  lifeCycleCardSx: {
    bg: widgetCardBg(appearance, "white"),
    borderRadius: CARD_RADIUS,
    boxShadow: CARD_SHADOW,
    py: 4,
    px: 4,
    border: "none",
    color: widgetBodyColor(appearance)
  },
  subHeadingSx: {
    pl: 2.5,
    mb: 2,
    ...optionalColor(appearance.widgetBodyTextColor),
    "&:last-of-type:not(:only-of-type)": {
      mt: 3
    }
  }
});

export const getWidgetStyleCategory = (
  selectedColorPalette?: string | null
): WidgetStyleCategory => {
  if (selectedColorPalette === "corporate") return "fresh";
  if (selectedColorPalette === "business") return "bold";
  return "classic";
};

const classicTheme = (
  appearance: AppearanceColors,
  siteColors: SiteThemeColors,
  accent?: string
): ProjectPageTheme => {
  const heading = siteColors.color3;
  const bodySx = heading ? { color: heading } : {};
  const buttonSx = accent ? classicPrimaryButtonSx(accent) : {};

  return {
    id: "classic",
    appearance,
    siteColors,
    pageSx: {
      ...bodySx,
      ...classicInteractiveSx(accent),
      ...optionalColor(appearance.toolBodyTextColor),
      ...toolMetaSx(appearance)
    },
    titleSx: heading ? { color: heading } : {},
    toolBannerTitleSx: toolBannerTitleSx(accent || "brand.500", true),
    bodySx,
    descriptionCardSx: {
      ...optionalBg(appearance.toolCardBackground)
    },
    breadcrumbSx: accent ? appearanceBreadcrumbSx(appearance) : {},
    toolsSx: {
      ...optionalBg(appearance.tabBackgroundColor)
    },
    tabListSx: accent
      ? {
          bg: "transparent",
          borderBottomWidth: "7px",
          borderBottomStyle: "solid",
          borderBottomColor: accent,
          pl: 0,
          pb: 0
        }
      : {},
    tabSx: accent
      ? {
          color: "#fff",
          bg: "#707070",
          textTransform: "uppercase",
          fontWeight: "bold",
          borderRadius: 0,
          _selected: {
            bg: accent,
            color: activeTabText(appearance)
          },
          _hover: {
            bg: accent,
            color: "#fff"
          }
        }
      : {},
    tabPanelSx: {},
    tabPanelContentSx: {
      px: 4,
      py: 6,
      bg: toolCardBg(appearance, "#fafafa"),
      ...bodySx,
      ...optionalColor(appearance.toolBodyTextColor)
    },
    widgetsColumnSx: {},
    widgets: classicWidgets(accent, bodySx, appearance),
    buttonSx
  };
};

const freshTheme = (
  brand: string,
  appearance: AppearanceColors,
  siteColors: SiteThemeColors
): ProjectPageTheme => {
  const widgets = freshWidgets(appearance);

  return {
    id: "fresh",
    appearance,
    siteColors,
    pageSx: {
      ...appearancePageSx(appearance),
      color: toolBodyColor(appearance),
      lineHeight: 1.6,
      ...toolMetaSx(appearance)
    },
    titleSx: {
      color: appearance.toolTitleTextColor || brand,
      fontWeight: "bold"
    },
    toolBannerTitleSx: toolBannerTitleSx(
      appearance.toolTitleTextColor || brand
    ),
    bodySx: { color: toolBodyColor(appearance) },
    breadcrumbSx: appearanceBreadcrumbSx(appearance),
    descriptionCardSx: {
      bg: toolCardBg(appearance, "#ffffff"),
      borderRadius: CARD_RADIUS,
      boxShadow: CARD_SHADOW,
      p: { base: 6, md: 16 },
      ...optionalColor(appearance.toolBodyTextColor)
    },
    toolsSx: {
      bg: tabCardBg(appearance, "#fafafa"),
      borderRadius: CARD_RADIUS,
      overflow: "hidden"
    },
    tabListSx: {
      bg: "transparent",
      borderBottomWidth: 0
    },
    tabSx: {
      color: appearance.toolTabTextColor || "#8c8c8c",
      bg: tabCardBg(appearance, "#fafafa"),
      borderBottomWidth: 0,
      px: 8,
      py: 5,
      _hover: {
        bg: tabCardBg(appearance, "#fafafa"),
        color: appearance.toolTabTextColor || "#8c8c8c"
      },
      _selected: {
        color: activeTabText(appearance),
        bg: appearance.toolTabBackgroundColorActive || brand,
        borderRadius: "6px",
        borderBottomWidth: 0,
        _hover: {
          color: activeTabText(appearance),
          bg: appearance.toolTabBackgroundColorActive || brand
        }
      }
    },
    tabPanelSx: {
      my: 4
    },
    tabPanelContentSx: {
      bg: toolCardBg(appearance, "#fff"),
      borderRadius: CARD_RADIUS,
      boxShadow: CARD_SHADOW,
      p: { base: 6, md: 16 },
      color: toolBodyColor(appearance)
    },
    widgetsColumnSx: widgets.listContainerSx,
    widgets,
    buttonSx: {}
  };
};

const boldTheme = (
  brand: string,
  appearance: AppearanceColors,
  siteColors: SiteThemeColors
): ProjectPageTheme => {
  const widgets = boldWidgets(appearance);

  return {
    id: "bold",
    appearance,
    siteColors,
    pageSx: {
      ...appearancePageSx(appearance),
      color: toolBodyColor(appearance),
      lineHeight: 1.6,
      ...toolMetaSx(appearance)
    },
    titleSx: {
      color: appearance.toolTitleTextColor || brand,
      fontWeight: "bold"
    },
    toolBannerTitleSx: toolBannerTitleSx(
      appearance.toolTitleTextColor || brand
    ),
    bodySx: { color: toolBodyColor(appearance) },
    breadcrumbSx: appearanceBreadcrumbSx(appearance),
    descriptionCardSx: {
      bg: "transparent",
      boxShadow: "none",
      borderRadius: 0,
      ...optionalColor(appearance.toolBodyTextColor)
    },
    toolsSx: {
      bg: tabCardBg(appearance, "#f3f3f3"),
      borderRadius: CARD_RADIUS,
      overflow: "hidden"
    },
    tabListSx: {
      bg: "#e2e2e2",
      borderBottomWidth: 0,
      px: 6,
      py: 4
    },
    tabSx: {
      color: appearance.toolTabTextColor || "#fff",
      bg: "#9b9b9b",
      borderRadius: CARD_RADIUS,
      borderBottomWidth: 0,
      px: 4,
      py: 1,
      mx: 1,
      _selected: {
        color: activeTabText(appearance),
        bg: appearance.toolTabBackgroundColorActive || brand,
        borderBottomWidth: 0,
        _hover: {
          color: activeTabText(appearance),
          bg: appearance.toolTabBackgroundColorActive || brand
        }
      }
    },
    tabPanelSx: {
      p: 6
    },
    tabPanelContentSx: {
      bg: toolCardBg(appearance, "#fff"),
      borderRadius: CARD_RADIUS,
      boxShadow: CARD_SHADOW,
      p: 6,
      color: toolBodyColor(appearance)
    },
    widgetsColumnSx: widgets.listContainerSx,
    widgets,
    buttonSx: {}
  };
};

export const getProjectPageTheme = (
  selectedColorPalette?: string | null,
  brandColor?: string | null,
  colorMapping?: Record<string, string> | null,
  siteTheme?: Partial<Theme> | SiteThemeColors | null
): ProjectPageTheme => {
  const id = getWidgetStyleCategory(selectedColorPalette);
  const paletteLoaded = selectedColorPalette !== undefined;
  const mergedTheme = {
    ...(siteTheme || {}),
    ...(typeof brandColor === "string" && brandColor ? { brandColor } : {})
  };
  const siteColors = paletteLoaded ? readSiteThemeColors(mergedTheme) : {};
  const brand = resolveBrand(brandColor, siteTheme);
  const accent =
    paletteLoaded && id === "classic"
      ? resolveAccentColor(mergedTheme)
      : undefined;
  const appearanceBrand =
    id === "classic"
      ? accent || (paletteLoaded ? brand : "currentColor")
      : brand;
  const appearance = resolveAppearanceColors(
    colorMapping,
    getThemeAppearanceDefaults(id, appearanceBrand),
    appearanceBrand
  );

  if (id === "fresh") return freshTheme(brand, appearance, siteColors);
  if (id === "bold") return boldTheme(brand, appearance, siteColors);
  return classicTheme(appearance, siteColors, accent);
};

export type { AppearanceColors } from "@/helpers/appearance-colors";
export type { SiteThemeColors } from "@/helpers/theme-api-colors";
export {
  appearancePageSx,
  getThemeAppearanceDefaults,
  resolveAppearanceColors
} from "@/helpers/appearance-colors";

export const getWidgetStyles = (
  selectedColorPalette?: string | null
): WidgetStyles => getProjectPageTheme(selectedColorPalette).widgets;
